import {
  patchPositionStructuralAssessment,
  Position,
  positionFieldName,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { useEffect, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { AlertCircle, Plus, PlusCircle } from "lucide-react";
import t from "@/lang/lang";
import { StructuralAssessment } from "@/services/backend/htz/inspection/types/structural-assessment";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { cn } from "@/shared/lib/utils";
import { Label } from "@/shared/components/ui/label";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { H4 } from "@/shared/components/typography";
import { Separator } from "@/shared/components/ui/separator";
import { ScrollArea, ScrollBar } from "@/shared/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Item,
  itemsForComponentKind,
} from "@/services/backend/htz/inspection/types/structural-assessment-catalog";
import { v4 } from "uuid";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useActiveStructuralAssessmentCatalog } from "@/services/backend/htz/inspection/hooks/use-active-structural-assessment-catalog";
import {
  countFieldErrors,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import {
  ResultDot,
  ResultSelect,
} from "@/services/backend/htz/inspection/components/inspection-result";
import { TableCard } from "@/shared/components/tables/table-card";
import { ComponentData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import {
  PatchStructuralAssessmentRequest,
  updateHtzWorkOrderPositionCache,
  useHtzWorkOrderAddAssessmentMutation,
  useHtzWorkOrderPatchStructuralAssessmentMutation,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { RemoveAssessmentDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/remove-assessment-dialog";

export function StructuralInspection({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const assessment = position.structuralAssessments.find(
    (a) =>
      a.id === assessmentId && a.componentId === activeComponent?.componentId,
  );

  return (
    <ScrollArea className="h-[65vh] space-y-2">
      <StructuralAssessmentTable
        workOrder={workOrder}
        position={position}
        activeComponent={activeComponent}
        selectedAssessmentId={assessmentId}
        onAssessmentSelectedId={setAssessmentId}
      />
      <div className="p-2 py-4">
        {assessment && (
          <StructuralAssessmentForm
            workOrder={workOrder}
            position={position}
            assessment={assessment}
          />
        )}
      </div>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
}

export function AddStructuralAssessmentButton({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const { data: structuralAssessmentCatalog } =
    useActiveStructuralAssessmentCatalog(workOrder.companyId);
  const [add, { error, isLoading, reset }] =
    useHtzWorkOrderAddAssessmentMutation();

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const { resetField } = useErrValidationFailedContext();
  const errorKey = positionFieldName(position, "structuralAssessments");

  if (!structuralAssessmentCatalog) {
    return null;
  }

  // The catalog item is part of the add request: the backend applies it
  // in the same transaction, so a failure cannot leave a blank assessment
  // behind.
  const doAdd = (item?: Item) => {
    if (isLoading || !activeComponent) return;

    add({
      id: position.workOrderId,
      positionId: position.id,
      type: "structural",
      assessmentId: v4(),
      componentId: activeComponent.componentId,
      structural: item && {
        requirement: item.requirement,
        deficiency: item.deficiency,
        recommendation: item.recommendation,
        result: item.result,
      },
    });
    resetField(errorKey);
  };

  const items = itemsForComponentKind(
    structuralAssessmentCatalog,
    activeComponent?.componentKindId ?? "",
  );

  const disabled =
    isImmutable(workOrder) || activeComponent === null || isLoading;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button>
          <PlusCircle />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Beurteilung hinzufügen")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => doAdd()}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Blanko")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("Aus Vorlage:")}</DropdownMenuLabel>
        {items.map((item) => (
          <DropdownMenuItem onClick={() => doAdd(item)} key={item.id}>
            <Plus className="mr-2 h-4 w-4" />
            <span className="flex items-center space-x-2">
              <ResultDot result={item.result} showLabel={false} />
              <span>{`${item.deficiency}`}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StructuralAssessmentTable({
  workOrder,
  position,
  activeComponent,
  selectedAssessmentId,
  onAssessmentSelectedId,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
  selectedAssessmentId: string | null;
  onAssessmentSelectedId: (a: string | null) => void;
}) {
  const toggle = (assessment: StructuralAssessment) => {
    if (assessment.id === selectedAssessmentId) {
      onAssessmentSelectedId(null);
    } else {
      onAssessmentSelectedId(assessment.id);
    }
  };

  const { fieldErrors } = useErrValidationFailedContext();

  return (
    <TableCard className="shadow-none">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8">{t("Bewertung")}</TableHead>
            <TableHead className="h-8">{t("Mangel")}</TableHead>
            <TableHead className="h-8">{t("")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {position.structuralAssessments
            .filter((a) => a.componentId === activeComponent?.componentId)
            .map((assessment) => (
              <TableRow
                key={assessment.id}
                className={cn(
                  "cursor-pointer",
                  assessment.id === selectedAssessmentId ? "bg-accent" : "",
                )}
              >
                <TableCell className="py-1" onClick={() => toggle(assessment)}>
                  <span className="flex items-center space-x-2">
                    {countFieldErrors(
                      fieldErrors,
                      positionFieldName(
                        position,
                        "structuralAssessments",
                        assessment.id,
                      ),
                    ) > 0 && <AlertCircle className="h-6 w-6 text-red-500" />}
                    <ResultDot result={assessment.result} />
                  </span>
                </TableCell>
                <TableCell
                  className="w-full py-1"
                  onClick={() => toggle(assessment)}
                >
                  {assessment.deficiency}
                </TableCell>
                <TableCell className="py-1 text-right">
                  <RemoveAssessmentDialog
                    position={position}
                    assessmentId={assessment.id}
                    title={t("Beurteilung löschen?")}
                    description={t(
                      "Die Beurteilung wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
                    )}
                    onRemoved={() => onAssessmentSelectedId(null)}
                    disabled={isImmutable(workOrder)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function StructuralAssessmentForm({
  workOrder,
  position,
  assessment,
}: {
  workOrder: WorkOrder;
  position: Position;
  assessment: StructuralAssessment;
}) {
  const request: PatchStructuralAssessmentRequest = {
    id: position.workOrderId,
    positionId: position.id,
    assessmentId: assessment.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzWorkOrderPositionCache,
    useHtzWorkOrderPatchStructuralAssessmentMutation(),
  );

  const { resetField } = useErrValidationFailedContext();
  const fieldName = (field: string) =>
    positionFieldName(position, "structuralAssessments", assessment.id, field);
  const disabled = isImmutable(workOrder);

  const onPatch = (
    patch: Partial<Omit<StructuralAssessment, "id" | "ahuId">>,
  ) =>
    set(
      { ...request, ...patch },
      patchPositionStructuralAssessment(position, { ...assessment, ...patch }),
    );

  return (
    <div className="h-full">
      <H4>{t("Beurteilung")}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid content-start gap-1.5">
          <Label>{t("Bewertung")}</Label>
          <ResultSelect
            result={assessment.result}
            onResultChange={(result) => {
              // The result decides which texts are required.
              resetField(fieldName("requirement"));
              resetField(fieldName("deficiency"));
              resetField(fieldName("recommendation"));
              onPatch({ result });
            }}
            fieldName={fieldName("result")}
            disabled={disabled}
          />
        </div>
        <TextareaField
          label={t("Anforderung")}
          value={assessment.requirement}
          onChange={(e) => onPatch({ requirement: e.target.value })}
          fieldName={fieldName("requirement")}
          disabled={disabled}
        />
        <TextareaField
          label={t("Mangel")}
          value={assessment.deficiency}
          onChange={(e) => onPatch({ deficiency: e.target.value })}
          fieldName={fieldName("deficiency")}
          disabled={disabled}
        />
        <TextareaField
          label={t("Handlungsempfehlung")}
          value={assessment.recommendation}
          onChange={(e) => onPatch({ recommendation: e.target.value })}
          fieldName={fieldName("recommendation")}
          disabled={disabled}
        />
        <TextareaField
          label={t("Bemerkung (intern)")}
          value={assessment.comment}
          onChange={(e) => onPatch({ comment: e.target.value })}
          fieldName={fieldName("comment")}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
